import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

interface FormState {
  label: string;
  billed: string;
  today_label: string;
  onboarding: string;
  platform: string;
  trust: string;
  will: string;
  commitment: string;
  thereafter: string;
  promo: string;
  promo_terms: string;
  promo_badge: string;
  show_platform_qualifier: boolean;
}

const FIELDS: Array<{
  key: keyof FormState;
  label: string;
  placeholder: string;
  numeric?: boolean;
  optIn?: boolean; // when true, field is empty by default (placeholder is just a hint)
}> = [
  { key: "label", label: "Plan label", placeholder: "Custom Plan" },
  {
    key: "billed",
    label: "Billed today (USD)",
    placeholder: "3000",
    numeric: true,
  },
  { key: "today_label", label: "Today label", placeholder: "Onboarding fee" },
  { key: "onboarding", label: "Onboarding fee", placeholder: "$3,000" },
  { key: "platform", label: "Platform fee", placeholder: "$500/mo" },
  { key: "trust", label: "Trust fee", placeholder: "$500" },
  { key: "will", label: "Will fee", placeholder: "$150" },
  { key: "commitment", label: "Commitment", placeholder: "Monthly" },
  {
    key: "promo",
    label: "Promotion title",
    placeholder: "$100 in credits applied",
    optIn: true,
  },
  {
    key: "promo_terms",
    label: "Promotion terms",
    placeholder: "Pay by May 5, 2026 to lock in this rate",
    optIn: true,
  },
  {
    key: "promo_badge",
    label: "Promotion badge",
    placeholder: "Limited time",
    optIn: true,
  },
  {
    key: "thereafter",
    label: "Order summary note",
    placeholder: "",
    optIn: true,
  },
];

const EMPTY_FORM: FormState = {
  label: "",
  billed: "",
  today_label: "",
  onboarding: "",
  platform: "",
  trust: "",
  will: "",
  commitment: "",
  thereafter: "",
  promo: "",
  promo_terms: "",
  promo_badge: "",
  show_platform_qualifier: false,
};

const DEFAULT_FORM: FormState = {
  ...FIELDS.reduce(
    (acc, f) => {
      acc[f.key] = f.optIn ? "" : f.placeholder;
      return acc;
    },
    { ...EMPTY_FORM } as any,
  ),
  show_platform_qualifier: false,
};

function CopyIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="4.5"
        y="4.5"
        width="8"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3 11V3.5A1.5 1.5 0 014.5 2H10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function StarterPayCustomPlanBuilderPage() {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [copied, setCopied] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string>("/starter-pay");

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    FIELDS.forEach((f) => {
      const v = ((form[f.key] as string) || "").trim();
      if (v) p.set(f.key, v);
    });
    if (form.show_platform_qualifier) p.set("show_platform_qualifier", "1");
    return p.toString();
  }, [form]);

  // The iframe previews the legacy /starter-pay view (it detects iframe context
  // and skips the redirect to /get-started-account). The shared URL points at
  // the new flow, where the custom params are stashed and round-tripped
  // through the email-verify step to /get-started-payment.
  const previewPath = `/starter-pay${queryString ? `?${queryString}` : ""}`;
  const sharePath = `/get-started-account${queryString ? `?${queryString}` : ""}`;
  const fullUrl = `${window.location.origin}${sharePath}`;

  // Debounce iframe updates so it doesn't reload on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setIframeUrl(previewPath), 500);
    return () => clearTimeout(t);
  }, [previewPath]);

  if (!isGetSnugDomain) return null;

  const setField = (key: keyof FormState, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available; user can still select the URL manually.
    }
  };

  const handleResetDefaults = () => setForm(DEFAULT_FORM);
  const handleClear = () => setForm(EMPTY_FORM);

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-builder">
        <header className="sp-builder-head">
          <div className="sp-eyebrow">
            <span className="sp-eyebrow-dot" aria-hidden="true" />
            Internal tool
          </div>
          <h1 className="sp-headline">
            Custom plan <em>builder.</em>
          </h1>
          <p className="sp-sub">
            Configure a custom Starter plan and share the generated URL with the
            customer. Empty fields fall back to the standard Annual
            configuration. The preview updates 500ms after you stop typing.
          </p>
        </header>

        <div className="sp-builder-grid">
          <div className="sp-builder-form">
            <div className="sp-card">
              <div className="sp-card-head">
                <h2 className="sp-section-title">Plan configuration</h2>
                <p className="sp-section-help">
                  Numbers are auto-formatted with $ and commas on the live page.
                  Use plain text (e.g., "Waived") for non-numeric values. Leave
                  a field blank to inherit the Annual default.
                </p>
              </div>

              <div className="sp-input-grid sp-input-grid-2">
                {FIELDS.map((f) => (
                  <label key={f.key} className="sp-input">
                    <span className="sp-input-label">{f.label}</span>
                    <input
                      type="text"
                      inputMode={f.numeric ? "numeric" : undefined}
                      className="sp-input-field"
                      value={form[f.key] as string}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value;
                        const next = f.numeric
                          ? raw.replace(/[^0-9]/g, "")
                          : raw;
                        setField(f.key, next);
                      }}
                      placeholder={f.placeholder}
                    />
                  </label>
                ))}
              </div>

              <label className="sp-input sp-input-toggle" style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.show_platform_qualifier}
                  onChange={(e) => setField("show_platform_qualifier", e.target.checked)}
                  style={{ width: 16, height: 16, cursor: "pointer" }}
                />
                <span className="sp-input-label" style={{ margin: 0 }}>
                  Show "starts month 4" qualifier on platform fee
                </span>
              </label>
            </div>

            <div className="sp-card">
              <div className="sp-card-head">
                <h2 className="sp-section-title">Generated URL</h2>
                <p className="sp-section-help">
                  Share this with the customer. The query params are stripped
                  from the address bar after the page loads, but the custom
                  configuration remains active.
                </p>
              </div>

              <div className="sp-url-box">
                <code>{fullUrl}</code>
              </div>

              <div className="sp-builder-actions">
                <button
                  type="button"
                  className="sp-cta sp-cta-secondary"
                  onClick={handleResetDefaults}
                >
                  Reset to defaults
                </button>
                <button
                  type="button"
                  className="sp-cta sp-cta-secondary"
                  onClick={handleClear}
                >
                  Clear all
                </button>
                <button
                  type="button"
                  className="sp-cta sp-cta-secondary"
                  onClick={handleCopy}
                >
                  <CopyIcon />
                  {copied ? "Copied" : "Copy URL"}
                </button>
              </div>
            </div>
          </div>

          <aside className="sp-builder-preview">
            <div className="sp-card">
              <div className="sp-card-head">
                <span className="sp-card-eyebrow">Live preview</span>
                <p className="sp-section-help" style={{ marginTop: 6 }}>
                  Pre-payment view of /starter-pay with the params above.
                </p>
              </div>
              <div className="sp-iframe-frame">
                <iframe
                  key={iframeUrl}
                  src={iframeUrl}
                  className="sp-iframe"
                  title="Custom plan preview"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
